import apiClient from './apiClient';
import { 
  Review, 
  ReviewSummary, 
  ReviewListResponse, 
  CreateReviewRequest, 
  UpdateReviewRequest, 
  ReviewFilterRequest, 
  RejectReviewRequest,
  ReviewTargetTypeValue 
} from '../types/review';

class ReviewService {
  // FIELD REVIEWS
  async createFieldReview(fieldId: string, request: CreateReviewRequest): Promise<Review> {
    return await apiClient.post<Review>(`/api/field-reviews/${fieldId}`, request);
  }

  async getFieldReviews(fieldId: string, filter?: ReviewFilterRequest): Promise<ReviewListResponse> {
    const params = new URLSearchParams();
    if (filter?.isVerified !== undefined && filter.isVerified !== null) params.append('isVerified', filter.isVerified.toString());
    if (filter?.isConfirmed !== undefined && filter.isConfirmed !== null) params.append('isConfirmed', filter.isConfirmed.toString());
    if (filter?.ratingFilter) params.append('ratingFilter', filter.ratingFilter.toString());
    if (filter?.sortBy) params.append('sortBy', filter.sortBy);
    if (filter?.sortOrder) params.append('sortOrder', filter.sortOrder);
    if (filter?.page) params.append('page', filter.page.toString());
    if (filter?.pageSize) params.append('pageSize', filter.pageSize.toString());
    
    return await apiClient.get<ReviewListResponse>(`/api/field-reviews/${fieldId}?${params.toString()}`);
  }

  async getFieldReviewSummary(fieldId: string): Promise<ReviewSummary> {
    return await apiClient.get<ReviewSummary>(`/api/field-reviews/${fieldId}/summary`);
  }

  async updateFieldReview(reviewId: string, request: UpdateReviewRequest): Promise<Review> {
    return await apiClient.put<Review>(`/api/field-reviews/${reviewId}`, request);
  }

  // REFEREE REVIEWS
  async createRefereeReview(refereeId: string, request: CreateReviewRequest): Promise<Review> {
    return await apiClient.post<Review>(`/api/referee-reviews/${refereeId}`, request);
  }

  async getRefereeReviews(refereeId: string, filter?: ReviewFilterRequest): Promise<ReviewListResponse> {
    const params = new URLSearchParams();
    if (filter?.isVerified !== undefined && filter.isVerified !== null) params.append('isVerified', filter.isVerified.toString());
    if (filter?.isConfirmed !== undefined && filter.isConfirmed !== null) params.append('isConfirmed', filter.isConfirmed.toString());
    if (filter?.ratingFilter) params.append('ratingFilter', filter.ratingFilter.toString());
    if (filter?.sortBy) params.append('sortBy', filter.sortBy);
    if (filter?.sortOrder) params.append('sortOrder', filter.sortOrder);
    if (filter?.page) params.append('page', filter.page.toString());
    if (filter?.pageSize) params.append('pageSize', filter.pageSize.toString());
    
    return await apiClient.get<ReviewListResponse>(`/api/referee-reviews/${refereeId}?${params.toString()}`);
  }

  async getRefereeReviewSummary(refereeId: string): Promise<ReviewSummary> {
    return await apiClient.get<ReviewSummary>(`/api/referee-reviews/${refereeId}/summary`);
  }

  async updateRefereeReview(reviewId: string, request: UpdateReviewRequest): Promise<Review> {
    return await apiClient.put<Review>(`/api/referee-reviews/${reviewId}`, request);
  }

  // OPERATION REVIEWS (for upcoming operations/events)
  async createOperationReview(operationId: string, request: CreateReviewRequest): Promise<Review> {
    return await apiClient.post<Review>(`/api/operation-reviews/${operationId}`, request);
  }

  async getOperationReviews(operationId: string, filter?: ReviewFilterRequest): Promise<ReviewListResponse> {
    const params = new URLSearchParams();
    if (filter?.isVerified !== undefined && filter.isVerified !== null) params.append('isVerified', filter.isVerified.toString());
    if (filter?.isConfirmed !== undefined && filter.isConfirmed !== null) params.append('isConfirmed', filter.isConfirmed.toString());
    if (filter?.ratingFilter) params.append('ratingFilter', filter.ratingFilter.toString());
    if (filter?.sortBy) params.append('sortBy', filter.sortBy);
    if (filter?.sortOrder) params.append('sortOrder', filter.sortOrder);
    if (filter?.page) params.append('page', filter.page.toString());
    if (filter?.pageSize) params.append('pageSize', filter.pageSize.toString());

    return await apiClient.get<ReviewListResponse>(`/api/operation-reviews/${operationId}?${params.toString()}`);
  }

  async getOperationReviewSummary(operationId: string): Promise<ReviewSummary> {
    return await apiClient.get<ReviewSummary>(`/api/operation-reviews/${operationId}/summary`);
  }

  async updateOperationReview(reviewId: string, request: UpdateReviewRequest): Promise<Review> {
    return await apiClient.put<Review>(`/api/operation-reviews/${reviewId}`, request);
  }

  // MISSION REVIEWS (for completed games/missions)
  async createMissionReview(missionId: string, request: CreateReviewRequest): Promise<Review> {
    return await apiClient.post<Review>(`/api/mission-reviews/${missionId}`, request);
  }

  async getMissionReviews(missionId: string, filter?: ReviewFilterRequest): Promise<ReviewListResponse> {
    const params = new URLSearchParams();
    if (filter?.isVerified !== undefined && filter.isVerified !== null) params.append('isVerified', filter.isVerified.toString());
    if (filter?.isConfirmed !== undefined && filter.isConfirmed !== null) params.append('isConfirmed', filter.isConfirmed.toString());
    if (filter?.ratingFilter) params.append('ratingFilter', filter.ratingFilter.toString());
    if (filter?.sortBy) params.append('sortBy', filter.sortBy);
    if (filter?.sortOrder) params.append('sortOrder', filter.sortOrder);
    if (filter?.page) params.append('page', filter.page.toString());
    if (filter?.pageSize) params.append('pageSize', filter.pageSize.toString());

    return await apiClient.get<ReviewListResponse>(`/api/mission-reviews/${missionId}?${params.toString()}`);
  }

  async getMissionReviewSummary(missionId: string): Promise<ReviewSummary> {
    return await apiClient.get<ReviewSummary>(`/api/mission-reviews/${missionId}/summary`);
  }

  async updateMissionReview(reviewId: string, request: UpdateReviewRequest): Promise<Review> {
    return await apiClient.put<Review>(`/api/mission-reviews/${reviewId}`, request);
  }

  // GAME REVIEWS (aliases for Mission reviews - for backwards compatibility)
  async createGameReview(gameId: string, request: CreateReviewRequest): Promise<Review> {
    return this.createMissionReview(gameId, request);
  }

  async getGameReviews(gameId: string, filter?: ReviewFilterRequest): Promise<ReviewListResponse> {
    return this.getMissionReviews(gameId, filter);
  }

  async getGameReviewSummary(gameId: string): Promise<ReviewSummary> {
    return this.getMissionReviewSummary(gameId);
  }

  async updateGameReview(reviewId: string, request: UpdateReviewRequest): Promise<Review> {
    return this.updateMissionReview(reviewId, request);
  }

  // MAP REVIEWS
  async createMapReview(mapId: string, request: CreateReviewRequest): Promise<Review> {
    return await apiClient.post<Review>(`/api/map-reviews/${mapId}`, request);
  }

  async getMapReviews(mapId: string, filter?: ReviewFilterRequest): Promise<ReviewListResponse> {
    const params = new URLSearchParams();
    if (filter?.isVerified !== undefined && filter.isVerified !== null) params.append('isVerified', filter.isVerified.toString());
    if (filter?.isConfirmed !== undefined && filter.isConfirmed !== null) params.append('isConfirmed', filter.isConfirmed.toString());
    if (filter?.ratingFilter) params.append('ratingFilter', filter.ratingFilter.toString());
    if (filter?.sortBy) params.append('sortBy', filter.sortBy);
    if (filter?.sortOrder) params.append('sortOrder', filter.sortOrder);
    if (filter?.page) params.append('page', filter.page.toString());
    if (filter?.pageSize) params.append('pageSize', filter.pageSize.toString());

    return await apiClient.get<ReviewListResponse>(`/api/map-reviews/${mapId}?${params.toString()}`);
  }

  async getMapReviewSummary(mapId: string): Promise<ReviewSummary> {
    return await apiClient.get<ReviewSummary>(`/api/map-reviews/${mapId}/summary`);
  }

  async updateMapReview(reviewId: string, request: UpdateReviewRequest): Promise<Review> {
    return await apiClient.put<Review>(`/api/map-reviews/${reviewId}`, request);
  }

  // IMAGE UPLOAD
  async uploadImages(files: File[]): Promise<string[]> {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    const response = await apiClient.postFormData<{ urls: string[] }>('/api/review-images/upload', formData);
    return response.urls;
  }

  async deleteImage(imageUrl: string): Promise<void> {
    await apiClient.delete(`/api/review-images?imageUrl=${encodeURIComponent(imageUrl)}`);
  }

  // UNIVERSAL METHODS
  async getReviews(targetType: ReviewTargetTypeValue, targetId: string, filter?: ReviewFilterRequest): Promise<ReviewListResponse> {
    switch (targetType) {
      case 'field':
        return this.getFieldReviews(targetId, filter);
      case 'referee':
        return this.getRefereeReviews(targetId, filter);
      case 'mission':
        return this.getOperationReviews(targetId, filter);
      case 'game':
        return this.getMissionReviews(targetId, filter);
      case 'map':
        return this.getMapReviews(targetId, filter);
      default:
        throw new Error(`Unknown target type: ${targetType}`);
    }
  }

  async getReviewSummary(targetType: ReviewTargetTypeValue, targetId: string): Promise<ReviewSummary> {
    switch (targetType) {
      case 'field':
        return this.getFieldReviewSummary(targetId);
      case 'referee':
        return this.getRefereeReviewSummary(targetId);
      case 'mission':
        return this.getOperationReviewSummary(targetId);
      case 'game':
        return this.getMissionReviewSummary(targetId);
      case 'map':
        return this.getMapReviewSummary(targetId);
      default:
        throw new Error(`Unknown target type: ${targetType}`);
    }
  }

  async getMyReview(targetType: ReviewTargetTypeValue, targetId: string): Promise<Review | null> {
    try {
      let endpoint = '';
      switch (targetType) {
        case 'field':
          endpoint = `/api/field-reviews/my/${targetId}`;
          break;
        case 'referee':
          endpoint = `/api/referee-reviews/my/${targetId}`;
          break;
        case 'mission':
          endpoint = `/api/operation-reviews/my/${targetId}`;
          break;
        case 'game':
          endpoint = `/api/mission-reviews/my/${targetId}`;
          break;
        case 'map':
          endpoint = `/api/map-reviews/my/${targetId}`;
          break;
      }
      const result = await apiClient.get<Review>(endpoint);

      // Если API возвращает пустой объект, возвращаем null
      if (result && (Object.keys(result).length === 0 || !result.id)) {
        return null;
      }

      return result;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }

}

const reviewService = new ReviewService();
export default reviewService;